/**
 * Deterministic dialogue → speaker attribution for Narrator Mode.
 * Parses quoted speech and attaches nearest roster name/alias speech tag.
 */

import type { BookCharacter } from "./characterRoster";
import { characterNameKeys } from "./characterRoster";
import {
  listChapterParagraphs,
  type ChapterDocContent,
  type ChapterParagraphRef,
} from "./chapterParagraphs";

export type DialogueSpan = {
  paragraphId: string;
  from: number;
  to: number;
  speakerId: string | "narrator";
  confidence: number;
};

const OPEN_QUOTES = new Set(['"', "\u201C", "\u2018", "'"]);
const CLOSE_QUOTES = new Set(['"', "\u201D", "\u2019", "'"]);

function isQuoteChar(ch: string): boolean {
  return OPEN_QUOTES.has(ch) || CLOSE_QUOTES.has(ch);
}

type QuoteRange = { from: number; to: number; text: string };

/** Extract quoted ranges within a single paragraph's plain text. */
export function extractQuotedRanges(text: string): QuoteRange[] {
  const ranges: QuoteRange[] = [];
  let i = 0;
  while (i < text.length) {
    const ch = text[i]!;
    if (!isQuoteChar(ch)) {
      i++;
      continue;
    }
    const open = ch;
    const close =
      open === "\u201C" ? "\u201D" : open === "\u2018" ? "\u2019" : open;
    const start = i;
    i++;
    while (i < text.length) {
      if (text[i] === close || (close === '"' && text[i] === '"') || (close === "'" && text[i] === "'")) {
        const end = i + 1;
        ranges.push({ from: start, to: end, text: text.slice(start, end) });
        i = end;
        break;
      }
      i++;
    }
  }
  return ranges;
}

type NameEntry = { characterId: string; keys: string[]; maxLen: number };

function buildNameIndex(roster: BookCharacter[]): NameEntry[] {
  return roster
    .map((c) => {
      const keys = characterNameKeys(c).filter(Boolean);
      const maxLen = keys.reduce((m, k) => Math.max(m, k.length), 0);
      return { characterId: c.id, keys, maxLen };
    })
    .filter((e) => e.keys.length > 0)
    .sort((a, b) => b.maxLen - a.maxLen);
}

function findSpeakerNearQuote(
  paragraphText: string,
  quote: QuoteRange,
  nameIndex: NameEntry[],
  prevSpeakerId: string | "narrator" | null
): { speakerId: string | "narrator"; confidence: number } {
  const before = paragraphText.slice(0, quote.from);
  const after = paragraphText.slice(quote.to);
  const windowBefore = before.slice(-120).toLowerCase();
  const windowAfter = after.slice(0, 80).toLowerCase();

  // Speech tags: said Name / Name said / Name asked …
  const tagPatterns = [
    /\b(?:said|asked|whispered|shouted|replied|answered|muttered|cried|called|snapped|murmured|continued|added|began|insisted|demanded|agreed|laughed|sighed)\s+([A-Z][A-Za-z'’-]+(?:\s+[A-Z][A-Za-z'’-]+)?)\b/,
    /\b([A-Z][A-Za-z'’-]+(?:\s+[A-Z][A-Za-z'’-]+)?)\s+(?:said|asked|whispered|shouted|replied|answered|muttered|cried|called|snapped|murmured|continued|added|began|insisted|demanded|agreed|laughed|sighed)\b/,
  ];

  for (const pattern of tagPatterns) {
    const searchSpaces = [windowBefore, windowAfter, before.toLowerCase()];
    for (const space of searchSpaces) {
      const match = space.match(pattern);
      if (!match?.[1]) continue;
      const nameKey = match[1].trim().toLowerCase().replace(/\s+/g, " ");
      for (const entry of nameIndex) {
        if (entry.keys.includes(nameKey)) {
          return { speakerId: entry.characterId, confidence: 0.9 };
        }
        // First name / last token match
        for (const key of entry.keys) {
          if (key === nameKey || key.endsWith(` ${nameKey}`) || key.startsWith(`${nameKey} `)) {
            return { speakerId: entry.characterId, confidence: 0.75 };
          }
        }
      }
    }
  }

  // Bare name near quote
  for (const entry of nameIndex) {
    for (const key of entry.keys) {
      if (key.length < 2) continue;
      if (windowBefore.includes(key) || windowAfter.includes(key)) {
        return { speakerId: entry.characterId, confidence: 0.55 };
      }
    }
  }

  // Continuation: same speaker as previous quote in chapter
  if (prevSpeakerId && prevSpeakerId !== "narrator") {
    return { speakerId: prevSpeakerId, confidence: 0.4 };
  }

  return { speakerId: "narrator", confidence: 0.2 };
}

/**
 * Attribute dialogue spans for a TipTap JSON chapter document.
 * Non-quoted prose is not spanned (rendered as narrator via absence of highlight or full-paragraph narrator).
 */
export function attributeDialogueInChapter(
  content: unknown,
  roster: BookCharacter[]
): DialogueSpan[] {
  if (!content || typeof content !== "object") return [];
  const doc = content as ChapterDocContent;
  if (doc.type !== "doc") return [];
  const paragraphs = listChapterParagraphs(doc);
  return attributeDialogueInParagraphs(paragraphs, roster);
}

export function attributeDialogueInParagraphs(
  paragraphs: ChapterParagraphRef[],
  roster: BookCharacter[]
): DialogueSpan[] {
  const nameIndex = buildNameIndex(roster);
  const spans: DialogueSpan[] = [];
  let prevSpeaker: string | "narrator" | null = null;

  for (const para of paragraphs) {
    const text = para.text ?? "";
    if (!text.trim()) continue;
    const quotes = extractQuotedRanges(text);
    if (quotes.length === 0) continue;

    for (const quote of quotes) {
      const { speakerId, confidence } = findSpeakerNearQuote(text, quote, nameIndex, prevSpeaker);
      spans.push({
        paragraphId: para.id,
        from: quote.from,
        to: quote.to,
        speakerId,
        confidence,
      });
      if (speakerId !== "narrator") prevSpeaker = speakerId;
    }
  }

  return spans;
}

/** Speakers that appear in dialogue spans (for the key panel). */
export function speakersInSpans(
  spans: DialogueSpan[],
  roster: BookCharacter[]
): Array<{ id: string | "narrator"; name: string }> {
  const seen = new Set<string>();
  const out: Array<{ id: string | "narrator"; name: string }> = [];
  let hasNarratorDialogue = false;
  for (const span of spans) {
    if (span.speakerId === "narrator") {
      hasNarratorDialogue = true;
      continue;
    }
    if (seen.has(span.speakerId)) continue;
    seen.add(span.speakerId);
    const character = roster.find((c) => c.id === span.speakerId);
    out.push({ id: span.speakerId, name: character?.name ?? span.speakerId });
  }
  // Always include Narrator for 3rd-person key
  out.unshift({ id: "narrator", name: "Narrator" });
  void hasNarratorDialogue;
  return out;
}

export function parseDialogueSpans(raw: unknown): DialogueSpan[] {
  if (!Array.isArray(raw)) return [];
  const out: DialogueSpan[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const rec = item as Record<string, unknown>;
    const paragraphId = typeof rec.paragraphId === "string" ? rec.paragraphId.trim() : "";
    const from = typeof rec.from === "number" ? rec.from : -1;
    const to = typeof rec.to === "number" ? rec.to : -1;
    const speakerId =
      rec.speakerId === "narrator"
        ? "narrator"
        : typeof rec.speakerId === "string"
          ? rec.speakerId.trim()
          : "";
    const confidence = typeof rec.confidence === "number" ? rec.confidence : 0;
    if (!paragraphId || from < 0 || to <= from || !speakerId) continue;
    out.push({ paragraphId, from, to, speakerId, confidence });
  }
  return out;
}
